import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import _ from "lodash";
import { Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { InputProcessorType } from "~app/common/components/input/input-processor";
import { SkogsvardAtgardsTyp } from "~app/common/models/constants";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { AtgardSkogsvardTradslag, AtgardSkogsvardTradslagPlanta } from "~app/features/virkeskop/models/skogsvard/skogsvard-models";
import { AtgardMediatorService } from "~app/features/virkeskop/services/atgard-mediator.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { SkogsvardForyngringService } from "~app/features/virkeskop/services/skogsvard-foryngring.service";
import { SkogsvardTradslagService } from "~app/features/virkeskop/services/skogsvard-tradslag.service";
import { canEdit } from "~app/role-permissions";
import { SkogsvardDetaljerForTradslagComponent } from "./skogsvard-detaljer-for-tradslag/skogsvard-detaljer-for-tradslag.component";
@Component({
    selector: "rask-skogsvard-detaljer-for",
    templateUrl: "./skogsvard-detaljer-for.component.html",
    styleUrls: ["./skogsvard-detaljer-for.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        InputNumberComponent,
        FormsModule,
        TextmultilineComponent,
        SkogsvardDetaljerForTradslagComponent
    ]
})
export class SkogsvardDetaljerForComponent implements OnInit, OnDestroy {
    protected kontraktReadonly = false;
    protected skogsvardsAtgard: Atgard;
    protected numberDecimalProcessor = InputProcessorType.numberDecimalProcessor(2, true);

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly skogsvardForyngringService = inject(SkogsvardForyngringService);
    private readonly skogsvardTradslagService = inject(SkogsvardTradslagService);

    private antalPlantor?: number;
    private foryngringTradslag: AtgardSkogsvardTradslag[] = [];

    private readonly canEdit = canEdit();

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        this.atgardMediatorService.atgard$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(atgard => {
                this.skogsvardsAtgard = atgard;
            });

        this.atgardMediatorService.atgardUpdate$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(atgard => {
                this.skogsvardsAtgard = atgard;
                this.setMalPlantorHa();
            });

        this.kontraktMediatorService.isReadonly$
            .pipe(
                map(readonly => readonly || !this.canEdit()),
                takeUntil(this.ngDestroyed$))
            .subscribe(readonly => this.kontraktReadonly = readonly);

        this.skogsvardTradslagService.skogsvardTradslag$.pipe(takeUntil(this.ngDestroyed$))
            .subscribe({
                next: x => {
                    this.updateraAntalplantor(x);
                },
                error: () => {
                //
                }
            });

        this.skogsvardForyngringService.skogsvardForyngringTradslag$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(f => {
                this.foryngringTradslag = f;
            });
    }

    protected getAntalPlantor(): string {
        return this.antalPlantor?.toString();
    }

    protected isSadd() {
        return this.skogsvardForyngringService.isSadd(this.skogsvardsAtgard);
    }

    protected onNettoarealChanged($event): void {
        if(!_.isNil($event)) {
            this.kontraktMediatorService.updateNettoarealSrc.next({
                atgardGuid: this.skogsvardsAtgard.guid,
                nettoareal: this.skogsvardsAtgard.nettoareal,
                atgardstyp: this.skogsvardsAtgard.atgardstyp,
                avverkningsanmalanGodkantDatum: null,
                beteckning: this.skogsvardsAtgard.beteckning
            });
            this.setMalPlantorHa();
            this.update();
        }
    }

    protected showTradslag(): boolean {
        return this.isPlantering();
    }

    private isPlantering(): boolean {
        return this.skogsvardsAtgard?.atgardstyp === SkogsvardAtgardsTyp.plantering;
    }

    private setMalPlantorHa(): void {
        if (this.isSadd()) {
            const malFrogiva = this.skogsvardForyngringService.getMalFrogiva(this.foryngringTradslag);
            this.skogsvardsAtgard.malFrogivaHa = malFrogiva === 0 ? null : malFrogiva;
        } else if (this.skogsvardsAtgard.nettoareal && this.skogsvardsAtgard.nettoareal > 0) {
            const antal = Math.floor(this.antalPlantor / this.skogsvardsAtgard.nettoareal);
            this.skogsvardsAtgard.malPlantorHa = Number.isNaN(antal) ? 0 : antal;
        }
    }

    private update(): void {
        this.atgardMediatorService.update(this.skogsvardsAtgard);
    }

    private updateraAntalplantor(plantor: AtgardSkogsvardTradslagPlanta[]) {
        let antal = 0;
        let malHa = 0;

        plantor.forEach(x => {
            antal += x.plantantal ?? 0;
            malHa += (x.malPlantorHA ?? 0)*((x.arealandel ?? 0)/100);
        });
        this.antalPlantor = antal;

        this.skogsvardsAtgard.malPlantorHa = Math.floor(malHa);
    }
}
